<script setup lang="ts">
import { useAuthStore } from "@/stores";
import { computed, ref } from "vue";
import navItems from "./nav-items";
import NavAwardList from "./NavAwardList.vue";
import useCandidate from "@/composables/use-candidate";
import useAwards from "@/composables/use-awards";
import useProducts from "@/composables/use-products";
import { Role } from "@/role";

const authStore = useAuthStore();

const { instance: candidateInstance, status: candidateStatus } = useCandidate();
const {
  awards,
  awardYear,
  awardsCeremonyOver,
  instance: awardInstance,
} = useAwards();
const { settings } = useAwards();
const { ceremonyTicket } = useProducts();

const items = computed(() => {
  let items = navItems[authStore.user.role] ?? navItems[Role.Unauthorized];
  if (authStore.isCandidate && !authStore.user.candidateVerified) {
    items = items.filter((i) => i.route.name === "home");
  }
  if (!candidateInstance.value?.voterRewards) {
    items = items.filter((i) => i.route.name !== "voter-rewards");
  }
  if (
    !awardInstance.value?.awardsCeremonyNights?.length ||
    !ceremonyTicket.value?.active ||
    !ceremonyTicket.value?.options
      .filter((o) => o.awardYear === awardYear.value)
      .every((o) => o.active) ||
    awardsCeremonyOver.value
  ) {
    items = items.filter((i) => i.route.name !== "awards-ceremony");
  }
  if (
    !candidateStatus.value.isFinalist &&
    !candidateStatus.value.isWinner &&
    !candidateStatus.value.isPreviousFinalist &&
    !candidateStatus.value.isPreviousWinner
  ) {
    items = items.filter((i) => i.route.name !== "plaques");
  }
  if (!settings.value?.stripePubKey) {
    items = items.filter((i) => i.route.name !== "accounting");
  }
  return items;
});

const showNav = ref(false);

const logout = async () => {
  authStore.logout();
};

const toggleNav = () => {
  showNav.value = !showNav.value;
};

const hideNav = () => {
  showNav.value = false;
};
</script>

<template>
  <div class="border-b border-gray-300 bg-white py-2 md:w-48 md:border-r">
    <div class="flex h-full flex-row justify-between md:flex-col">
      <div
        class="flex w-full items-center justify-between md:flex-col md:justify-start"
      >
        <div class="mx-2 border-gray-300 px-2 py-0 md:border-b md:pb-4">
          <router-link to="/">
            <img :src="settings?.logoUrl" class="mx-auto w-16 md:w-52" />
          </router-link>
        </div>

        <div
          class="hidden py-2 md:flex md:h-full md:flex-col md:justify-between"
        >
          <div class="flex w-44 flex-col items-start gap-y-2">
            <router-link
              v-for="item in items"
              :key="item.text"
              :to="item.route"
              class="w-full rounded px-4 py-1 text-gray-700"
              :class="{
                'bg-gray-300': item.route.name === $route.name,
                'font-bold': item.route.name === $route.name,
              }"
              >{{ item.text }}</router-link
            >
          </div>
        </div>
        <div class="md:hidden">
          <Button
            icon="bars"
            color="transparent"
            class="text-3xl text-gray-800"
            @click="toggleNav"
          ></Button>
          <div
            v-if="showNav"
            class="top-23 absolute right-1 z-50 flex w-52 flex-col gap-y-2 rounded border border-gray-400 bg-gray-50 p-4 shadow"
            v-click-outside="hideNav"
          >
            <router-link
              v-for="item in items"
              :key="item.text"
              :to="item.route"
              class="rounded p-2 text-lg text-gray-700"
              :class="{
                'bg-gray-300': item.route.name === $route.name,
                'font-bold': item.route.name === $route.name,
              }"
              @click="hideNav"
              >{{ item.text }}</router-link
            >
            <a
              v-if="authStore.authenticated"
              href="#"
              class="p-2 text-lg text-gray-700"
              @click="logout"
              >Log out</a
            >
            <NavAwardList v-if="authStore.isAdmin && awards?.length" />
          </div>
        </div>
      </div>
      <div class="hidden flex-col gap-y-1 py-2 md:flex">
        <a
          v-if="authStore.authenticated"
          href="#"
          class="pb-2 pl-4 text-gray-700"
          @click="logout"
          >Log out</a
        >
        <div class="px-2">
          <NavAwardList v-if="authStore.isAdmin && awards?.length" />
        </div>
      </div>
    </div>
  </div>
</template>
